import { useState, useEffect } from "react";
import { useTranslation } from "react-i18next";
import {
  X,
  CheckCircle,
  User,
  Globe,
  Phone,
  Mail,
  Briefcase,
  FileText,
  AlertCircle,
  Eye,
  Upload,
} from "lucide-react";
import Input from "@/components/ui/Input";
import Select from "@/components/ui/Select";
import Button from "@/components/ui/Button";
import { toast } from "sonner";

const InvestmentModal = ({
  isOpen,
  onClose,
  selectedSector,
  onSubmit,
  companiesList,
}) => {
  const { i18n } = useTranslation("common");
  const currentLang = i18n.language || "ar";

  const [formData, setFormData] = useState({
    nationalId: "",
    idType: "nationalId",
    nationality: "",
    investmentAmount: "",
    companyName: "",
    email: "",
    phone: "",
    fullName: "",
  });

  const [files, setFiles] = useState({
    identityPhoto: null,
    commercialRegister: null,
    taxCard: null,
  });

  const [filePreviews, setFilePreviews] = useState({
    identityPhoto: null,
    commercialRegister: null,
    taxCard: null,
  });

  const [errors, setErrors] = useState({});
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [currentStep, setCurrentStep] = useState(1);

  // تعريف الأنواع المسموح بها
  const ALLOWED_TYPES = {
    images: ["image/jpeg", "image/png", "image/jpg", "image/webp"],
    pdf: ["application/pdf"],
    documents: [
      "application/pdf",
      "application/msword",
      "application/vnd.openxmlformats-officedocument.wordprocessingml.document",
      "application/vnd.ms-excel",
      "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
    ],
  };

  const MAX_FILE_SIZE = 5 * 1024 * 1024; // 5MB لكل ملف
  const TOTAL_MAX_SIZE = 15 * 1024 * 1024; // 15MB إجمالي

  const translations = {
    ar: {
      investmentRequest: "طلب استثمار",
      idType: "نوع الهوية",
      nationalId: "الرقم القومي",
      passport: "جواز السفر",
      nationality: "الجنسية",
      investmentAmount: "المبلغ (بالمليون)",
      companyName: "اسم الشركة",
      email: "البريد الإلكتروني",
      phone: "رقم الهاتف",
      fullName: "الاسم بالكامل",
      identityPhoto: "صورة تحقيق الشخصية",
      commercialRegister: "السجل التجاري",
      taxCard: "البطاقة الضريبية",
      submit: "إرسال الطلب",
      cancel: "إلغاء",
      submitting: "جاري الإرسال...",
      required: "هذا الحقل مطلوب",
      invalidEmail: "البريد الإلكتروني غير صالح",
      invalidPhone: "رقم الهاتف غير صالح",
      invalidAmount: "المبلغ يجب أن يكون رقم موجب",
      identityPhotoRequired: "صورة تحقيق الشخصية مطلوبة",
      commercialRegisterRequired: "السجل التجاري مطلوب",
      taxCardRequired: "البطاقة الضريبية مطلوبة",
      step1: "البيانات الشخصية",
      step2: "بيانات الاستثمار",
      step3: "المستندات المطلوبة",
      next: "التالي",
      previous: "السابق",
      finalStep: "إرسال الطلب",
      allowedFiles: "الملفات المسموح بها",
      maxSize: "الحد الأقصى",
      imageFiles: "ملفات الصور",
      pdfFiles: "ملفات PDF",
      docFiles: "ملفات Word و Excel",
      fileTooLarge: "حجم الملف كبير جدًا",
      invalidFileType: "نوع الملف غير مسموح به",
      totalSizeExceeded: "إجمالي حجم الملفات تجاوز الحد المسموح",
      viewFile: "عرض الملف",
      removeFile: "إزالة",
      dragAndDrop: "اسحب وأفلت الملف هنا",
      orClick: "أو انقر للاختيار",
      fileTypes: "أنواع الملفات",
      sizeLimit: "حد الحجم",
    },
    en: {
      investmentRequest: "Investment Request",
      idType: "ID Type",
      nationalId: "National ID",
      passport: "Passport",
      nationality: "Nationality",
      investmentAmount: "Amount (in millions)",
      companyName: "Company Name",
      email: "Email",
      phone: "Phone Number",
      fullName: "Full Name",
      identityPhoto: "Identity Photo",
      commercialRegister: "Commercial Register",
      taxCard: "Tax Card",
      submit: "Submit Request",
      cancel: "Cancel",
      submitting: "Submitting...",
      required: "This field is required",
      invalidEmail: "Invalid email address",
      invalidPhone: "Invalid phone number",
      invalidAmount: "Amount must be a positive number",
      identityPhotoRequired: "Identity photo is required",
      commercialRegisterRequired: "Commercial register is required",
      taxCardRequired: "Tax card is required",
      step1: "Personal Data",
      step2: "Investment Data",
      step3: "Required Documents",
      next: "Next",
      previous: "Previous",
      finalStep: "Submit Request",
      allowedFiles: "Allowed Files",
      maxSize: "Maximum Size",
      imageFiles: "Image Files",
      pdfFiles: "PDF Files",
      docFiles: "Word & Excel Files",
      fileTooLarge: "File is too large",
      invalidFileType: "File type not allowed",
      totalSizeExceeded: "Total file size exceeded allowed limit",
      viewFile: "View File",
      removeFile: "Remove",
      dragAndDrop: "Drag and drop file here",
      orClick: "or click to select",
      fileTypes: "File Types",
      sizeLimit: "Size Limit",
    },
  };

  const tr = translations[currentLang] || translations.ar;

  // دالة للتحقق من صحة الملف
  const validateFile = (file, fileType) => {
    const errors = [];

    // التحقق من الحجم
    if (file.size > MAX_FILE_SIZE) {
      errors.push(`${tr.fileTooLarge} (${formatFileSize(MAX_FILE_SIZE)})`);
    }

    // التحقق من النوع حسب نوع الملف
    let allowedTypes = [];
    switch (fileType) {
      case "identityPhoto":
        allowedTypes = [...ALLOWED_TYPES.images, ...ALLOWED_TYPES.pdf];
        break;
      case "commercialRegister":
      case "taxCard":
        allowedTypes = [...ALLOWED_TYPES.documents, ...ALLOWED_TYPES.images];
        break;
      default:
        allowedTypes = [...ALLOWED_TYPES.documents, ...ALLOWED_TYPES.images];
    }

    if (!allowedTypes.includes(file.type)) {
      errors.push(tr.invalidFileType);
    }

    return errors;
  };

  // دالة لحساب إجمالي حجم الملفات
  const calculateTotalSize = () => {
    let total = 0;
    Object.values(files).forEach((file) => {
      if (file) total += file.size;
    });
    return total;
  };

  const formatFileSize = (bytes) => {
    if (bytes === 0) return "0 Bytes";
    const k = 1024;
    const sizes = ["Bytes", "KB", "MB", "GB"];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + " " + sizes[i];
  };

  const validateStep = (step) => {
    const newErrors = {};

    if (step === 1) {
      if (!formData.fullName.trim()) newErrors.fullName = tr.required;
      if (!formData.nationalId.trim()) newErrors.nationalId = tr.required;
      if (!formData.nationality.trim()) newErrors.nationality = tr.required;

      // تحقق من البريد الإلكتروني
      if (!formData.email.trim()) {
        newErrors.email = tr.required;
      } else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(formData.email)) {
        newErrors.email = tr.invalidEmail;
      }

      // تحقق من رقم الهاتف
      const phone = formData.phone.replace(/\D/g, ""); // نشيل أي حاجة مش رقم

      if (!phone) {
        newErrors.phone = tr.required;
      } else if (phone.length !== 11) {
        // الأرقام المصرية كلها 10 أرقام
        newErrors.phone = tr.invalidPhone;
      }
    }

    if (step === 2) {
      // تحقق من المبلغ
      const amount = parseFloat(formData.investmentAmount);
      if (!formData.investmentAmount.trim()) {
        newErrors.investmentAmount = tr.required;
      } else if (isNaN(amount) || amount <= 0) {
        newErrors.investmentAmount = tr.invalidAmount;
      }

      if (!formData.companyName.trim()) newErrors.companyName = tr.required;
    }

    if (step === 3) {
      // تحقق من الملفات
      if (!files.identityPhoto)
        newErrors.identityPhoto = tr.identityPhotoRequired;
      if (!files.commercialRegister)
        newErrors.commercialRegister = tr.commercialRegisterRequired;
      if (!files.taxCard) newErrors.taxCard = tr.taxCardRequired;

      // تحقق من إجمالي الحجم
      const totalSize = calculateTotalSize();
      if (totalSize > TOTAL_MAX_SIZE) {
        newErrors.files = tr.totalSizeExceeded;
      }
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleNext = () => {
    if (validateStep(currentStep)) {
      setCurrentStep(currentStep + 1);
    }
  };

  const handlePrevious = () => {
    setCurrentStep(currentStep - 1);
  };

  const handleFileChange = async (field, file) => {
    if (!file) {
      setFiles((prev) => ({ ...prev, [field]: null }));
      setFilePreviews((prev) => ({ ...prev, [field]: null }));
      return;
    }

    // التحقق من صحة الملف
    const fileErrors = validateFile(file, field);
    if (fileErrors.length > 0) {
      setErrors((prev) => ({ ...prev, [field]: fileErrors.join(", ") }));
      return;
    }

    // إنشاء preview للصور
    if (file.type.startsWith("image/")) {
      const reader = new FileReader();
      reader.onloadend = () => {
        setFilePreviews((prev) => ({ ...prev, [field]: reader.result }));
      };
      reader.readAsDataURL(file);
    } else {
      setFilePreviews((prev) => ({ ...prev, [field]: null }));
    }

    // تحديث حالة الملفات
    setFiles((prev) => ({ ...prev, [field]: file }));

    // مسح الخطأ إذا كان موجودًا
    if (errors[field]) {
      setErrors((prev) => ({ ...prev, [field]: null }));
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!validateStep(3)) {
      toast.error("يوجد أخطاء في البيانات، يرجى تصحيحها");
      return;
    }

    setIsSubmitting(true);
    try {
      const payload = new FormData();

      // إضافة البيانات النصية
      Object.entries(formData).forEach(([key, value]) => {
        payload.append(key, value || "");
      });

      // إضافة القطاع إذا كان محددًا
      if (selectedSector) {
        payload.append(
          "sector",
          selectedSector.sector_ar || selectedSector.sector_en || ""
        );
      }

      // إضافة الملفات
      Object.entries(files).forEach(([key, file]) => {
        if (file) {
          payload.append(key, file);
        }
      });

      // إرسال البيانات
      const resp = await fetch("https://nspo-backend-gray.vercel.app/api/investor", {
        method: "POST",
        body: payload,
      });

      const result = await resp.json().catch(() => null);

      if (!resp.ok) {
        throw new Error(result?.error || `خطأ في الإرسال (${resp.status})`);
      }

      toast.success("تم إرسال طلب الاستثمار بنجاح!");

      // استدعاء الدالة الموردة من الأب
      onSubmit(formData);

      // إغلاق المودال بعد 2 ثانية
      setTimeout(() => {
        onClose();
      }, 2000);
    } catch (err) {
      console.error("Submit error:", err);
      toast.error(err.message || "فشل في إرسال الطلب");
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleInputChange = (field, value) => {
    setFormData((prev) => ({ ...prev, [field]: value }));
    if (errors[field]) {
      setErrors((prev) => ({ ...prev, [field]: null }));
    }
  };

  const removeFile = (field) => {
    setFiles((prev) => ({ ...prev, [field]: null }));
    setFilePreviews((prev) => ({ ...prev, [field]: null }));
  };

  const viewFile = (field) => {
    const file = files[field];
    if (file) {
      const url = URL.createObjectURL(file);
      window.open(url, "_blank");
    }
  };

  useEffect(() => {
    if (!isOpen) {
      setFormData({
        nationalId: "",
        idType: "nationalId",
        nationality: "",
        investmentAmount: "",
        companyName: "",
        email: "",
        phone: "",
        fullName: "",
      });
      setFiles({
        identityPhoto: null,
        commercialRegister: null,
        taxCard: null,
      });
      setFilePreviews({
        identityPhoto: null,
        commercialRegister: null,
        taxCard: null,
      });
      setErrors({});
      setCurrentStep(1);
    }
  }, [isOpen]);

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 z-50 flex items-center justify-center p-4">
      <div className="bg-white dark:bg-gray-900 rounded-xl max-w-2xl w-full max-h-[90vh] overflow-y-auto">
        <div className="flex items-center justify-between p-6 border-b border-gray-200 dark:border-gray-700">
          <div>
            <h2 className="text-2xl font-bold text-gray-900 dark:text-white">
              {tr.investmentRequest}
            </h2>
            {selectedSector && (
              <p className="text-sm text-gray-600 dark:text-gray-300 mt-1">
                {selectedSector[`sector_${currentLang}`] ||
                  selectedSector.sector_ar}
              </p>
            )}
          </div>
          <button
            onClick={onClose}
            className="text-gray-400 hover:text-gray-600 dark:hover:text-gray-300 transition-colors"
          >
            <X className="w-6 h-6" />
          </button>
        </div>

        <div className="px-6 py-4 border-b border-gray-200 dark:border-gray-700">
          <div className="flex items-center justify-between">
            {[1, 2, 3, 4].map((step) => (
              <div key={step} className="flex items-center">
                <div
                  className={`w-8 h-8 rounded-full flex items-center justify-center text-sm font-medium ${
                    step === currentStep
                      ? "bg-blue-600 text-white"
                      : step < currentStep
                      ? "bg-green-500 text-white"
                      : "bg-gray-300 text-gray-600 dark:bg-gray-700 dark:text-gray-400"
                  }`}
                >
                  {step < currentStep ? (
                    <CheckCircle className="w-4 h-4" />
                  ) : (
                    step
                  )}
                </div>
                {step < 4 && (
                  <div
                    className={`w-12 h-1 mx-2 ${
                      step < currentStep
                        ? "bg-green-500"
                        : "bg-gray-300 dark:bg-gray-700"
                    }`}
                  />
                )}
              </div>
            ))}
          </div>
        </div>

        <div className="p-6">
          <form onSubmit={handleSubmit}>
            {currentStep === 1 && (
              <div className="space-y-4">
                <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-4">
                  {tr.step1}
                </h3>
                <Input
                  label={`${tr.fullName} *`}
                  value={formData.fullName}
                  onChange={(e) =>
                    handleInputChange("fullName", e.target.value)
                  }
                  error={errors.fullName}
                  placeholder="أدخل الاسم بالكامل"
                  icon={<User className="w-4 h-4" />}
                  required
                />
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                      {tr.idType} *
                    </label>
                    <Select
                      value={formData.idType}
                      onChange={(e) =>
                        handleInputChange("idType", e.target.value)
                      }
                      options={[
                        { value: "nationalId", label: tr.nationalId },
                        { value: "passport", label: tr.passport },
                      ]}
                    />
                  </div>
                  <Input
                    label={
                      formData.idType === "nationalId"
                        ? `${tr.nationalId} *`
                        : `${tr.passport} *`
                    }
                    value={formData.nationalId}
                    onChange={(e) =>
                      handleInputChange("nationalId", e.target.value)
                    }
                    error={errors.nationalId}
                    placeholder={
                      formData.idType === "nationalId"
                        ? "14 رقماً"
                        : "رقم جواز السفر"
                    }
                    required
                  />
                </div>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <Input
                    label={`${tr.nationality} *`}
                    value={formData.nationality}
                    onChange={(e) =>
                      handleInputChange("nationality", e.target.value)
                    }
                    error={errors.nationality}
                    placeholder="مثال: مصري، سعودي"
                    icon={<Globe className="w-4 h-4" />}
                    required
                  />
                  <Input
                    label={`${tr.phone} *`}
                    value={formData.phone}
                    onChange={(e) => handleInputChange("phone", e.target.value)}
                    error={errors.phone}
                    placeholder="+20 123 456 7890"
                    icon={<Phone className="w-4 h-4" />}
                    required
                  />
                </div>
                <Input
                  label={`${tr.email} *`}
                  type="email"
                  value={formData.email}
                  onChange={(e) => handleInputChange("email", e.target.value)}
                  error={errors.email}
                  placeholder="example@company.com"
                  icon={<Mail className="w-4 h-4" />}
                  required
                />
              </div>
            )}

            {currentStep === 2 && (
              <div className="space-y-4">
                <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-4">
                  {tr.step2}
                </h3>
                <Input
                  label={`${tr.investmentAmount} *`}
                  type="number"
                  min="1"
                  step="0.1"
                  value={formData.investmentAmount}
                  onChange={(e) =>
                    handleInputChange("investmentAmount", e.target.value)
                  }
                  error={errors.investmentAmount}
                  placeholder="مثال: 50 (يعني 50 مليون جنيه)"
                  icon={<Briefcase className="w-4 h-4" />}
                  required
                />
                <Select
                  label={`${tr.companyName} *`}
                  value={formData.companyName}
                  onChange={(e) =>
                    handleInputChange("companyName", e.target.value)
                  }
                  error={errors.companyName}
                  options={[
                    { value: "", label: "اختر الشركة" },
                    ...companiesList.map((company) => ({
                      value: company,
                      label: company,
                    })),
                  ]}
                  required
                />
              </div>
            )}

            {currentStep === 3 && (
              <div className="space-y-6">
                <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-4">
                  {tr.step3}
                </h3>

                {/* معلومات الملفات المسموحة */}
                <div className="bg-blue-50 dark:bg-blue-900/20 border border-blue-200 dark:border-blue-800 rounded-lg p-4">
                  <div className="flex items-center gap-2 mb-2">
                    <FileText className="w-5 h-5 text-blue-600 dark:text-blue-400" />
                    <h4 className="font-medium text-blue-800 dark:text-blue-300">
                      {tr.allowedFiles}
                    </h4>
                  </div>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-3 text-sm">
                    <div>
                      <p className="font-medium text-gray-700 dark:text-gray-300 mb-1">
                        {tr.fileTypes}:
                      </p>
                      <ul className="text-gray-600 dark:text-gray-400 space-y-1">
                        <li className="flex items-center gap-2">
                          <div className="w-2 h-2 bg-blue-500 rounded-full"></div>
                          {tr.imageFiles}: JPG, PNG, WEBP
                        </li>
                        <li className="flex items-center gap-2">
                          <div className="w-2 h-2 bg-green-500 rounded-full"></div>
                          {tr.pdfFiles}: PDF
                        </li>
                        <li className="flex items-center gap-2">
                          <div className="w-2 h-2 bg-purple-500 rounded-full"></div>
                          {tr.docFiles}: DOC, DOCX, XLS, XLSX
                        </li>
                      </ul>
                    </div>
                    <div>
                      <p className="font-medium text-gray-700 dark:text-gray-300 mb-1">
                        {tr.sizeLimit}:
                      </p>
                      <ul className="text-gray-600 dark:text-gray-400 space-y-1">
                        <li className="flex items-center gap-2">
                          <div className="w-2 h-2 bg-yellow-500 rounded-full"></div>
                          {tr.maxSize}: {formatFileSize(MAX_FILE_SIZE)} لكل ملف
                        </li>
                        <li className="flex items-center gap-2">
                          <div className="w-2 h-2 bg-red-500 rounded-full"></div>
                          {tr.maxSize}: {formatFileSize(TOTAL_MAX_SIZE)} إجمالي
                        </li>
                      </ul>
                    </div>
                  </div>
                </div>

                {errors.files && (
                  <div className="bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-lg p-3">
                    <div className="flex items-center gap-2">
                      <AlertCircle className="w-5 h-5 text-red-600 dark:text-red-400" />
                      <p className="text-red-700 dark:text-red-300 text-sm">
                        {errors.files}
                      </p>
                    </div>
                  </div>
                )}

                {/* حقول رفع الملفات */}
                {[
                  {
                    key: "identityPhoto",
                    label: tr.identityPhoto,
                    required: true,
                    accept: "image/*,.pdf",
                    description:
                      "صورة البطاقة الشخصية أو جواز السفر أو صورة شخصية",
                  },
                  {
                    key: "commercialRegister",
                    label: tr.commercialRegister,
                    required: true,
                    accept: ".pdf,.jpg,.jpeg,.png,.doc,.docx,.xls,.xlsx",
                    description: "صورة من السجل التجاري للشركة",
                  },
                  {
                    key: "taxCard",
                    label: tr.taxCard,
                    required: true,
                    accept: ".pdf,.jpg,.jpeg,.png,.doc,.docx,.xls,.xlsx",
                    description: "صورة من البطاقة الضريبية للشركة",
                  },
                ].map(({ key, label, required, accept, description }) => (
                  <div key={key} className="space-y-2">
                    <label className="block text-sm font-medium text-gray-700 dark:text-gray-300">
                      {label} {required && "*"}
                    </label>

                    {files[key] ? (
                      <div className="border border-gray-200 dark:border-gray-700 rounded-lg p-4">
                        <div className="flex items-center justify-between">
                          <div className="flex items-center gap-3">
                            <div className="p-2 bg-blue-100 dark:bg-blue-900/30 rounded-lg">
                              <FileText className="w-6 h-6 text-blue-600 dark:text-blue-400" />
                            </div>
                            <div>
                              <p className="font-medium text-gray-900 dark:text-white">
                                {files[key].name}
                              </p>
                              <p className="text-sm text-gray-600 dark:text-gray-400">
                                {formatFileSize(files[key].size)} •{" "}
                                {files[key].type}
                              </p>
                            </div>
                          </div>
                          <div className="flex items-center gap-2">
                            <button
                              type="button"
                              onClick={() => viewFile(key)}
                              className="p-2 text-blue-600 hover:text-blue-800 dark:text-blue-400 dark:hover:text-blue-300 transition-colors"
                              title={tr.viewFile}
                            >
                              <Eye className="w-5 h-5" />
                            </button>
                            <button
                              type="button"
                              onClick={() => removeFile(key)}
                              className="p-2 text-red-600 hover:text-red-800 dark:text-red-400 dark:hover:text-red-300 transition-colors"
                              title={tr.removeFile}
                            >
                              <X className="w-5 h-5" />
                            </button>
                          </div>
                        </div>

                        {filePreviews[key] &&
                          files[key].type.startsWith("image/") && (
                            <div className="mt-4">
                              <img
                                src={filePreviews[key]}
                                alt={label}
                                className="max-h-48 rounded-lg mx-auto border border-gray-200 dark:border-gray-700"
                              />
                            </div>
                          )}
                      </div>
                    ) : (
                      <div className="border-2 border-dashed border-gray-300 dark:border-gray-700 rounded-lg p-8 text-center hover:border-blue-500 dark:hover:border-blue-400 transition-colors cursor-pointer">
                        <label className="cursor-pointer block">
                          <div className="space-y-3">
                            <div className="mx-auto w-12 h-12 bg-gray-100 dark:bg-gray-800 rounded-full flex items-center justify-center">
                              <Upload className="w-6 h-6 text-gray-500 dark:text-gray-400" />
                            </div>
                            <div>
                              <p className="text-gray-700 dark:text-gray-300 font-medium">
                                {tr.dragAndDrop}
                              </p>
                              <p className="text-gray-500 dark:text-gray-400 text-sm mt-1">
                                {tr.orClick}
                              </p>
                            </div>
                            <p className="text-xs text-gray-500 dark:text-gray-400">
                              {description}
                            </p>
                          </div>
                          <input
                            type="file"
                            accept={accept}
                            onChange={(e) =>
                              handleFileChange(key, e.target.files?.[0] || null)
                            }
                            className="hidden"
                          />
                        </label>
                      </div>
                    )}

                    {errors[key] && (
                      <p className="text-sm text-red-600 dark:text-red-400">
                        {errors[key]}
                      </p>
                    )}
                  </div>
                ))}

                {/* إجمالي حجم الملفات */}
                <div className="bg-gray-50 dark:bg-gray-800 rounded-lg p-4">
                  <div className="flex items-center justify-between">
                    <span className="text-gray-700 dark:text-gray-300">
                      إجمالي حجم الملفات:
                    </span>
                    <span
                      className={`font-medium ${
                        calculateTotalSize() > TOTAL_MAX_SIZE
                          ? "text-red-600 dark:text-red-400"
                          : "text-green-600 dark:text-green-400"
                      }`}
                    >
                      {formatFileSize(calculateTotalSize())} /{" "}
                      {formatFileSize(TOTAL_MAX_SIZE)}
                    </span>
                  </div>
                  <div className="mt-2 w-full bg-gray-200 dark:bg-gray-700 rounded-full h-2">
                    <div
                      className={`h-2 rounded-full ${
                        calculateTotalSize() > TOTAL_MAX_SIZE
                          ? "bg-red-500"
                          : "bg-green-500"
                      }`}
                      style={{
                        width: `${Math.min(
                          (calculateTotalSize() / TOTAL_MAX_SIZE) * 100,
                          100
                        )}%`,
                      }}
                    />
                  </div>
                </div>
              </div>
            )}

            {currentStep === 4 && (
              <div className="space-y-4">
                <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-4">
                  {tr.finalStep}
                </h3>
                <div className="bg-gray-50 dark:bg-gray-800 rounded-lg p-4 space-y-6">
                  {/* البيانات الشخصية */}
                  <div>
                    <h4 className="font-semibold text-gray-900 dark:text-white mb-3 pb-2 border-b border-gray-200 dark:border-gray-700">
                      البيانات الشخصية:
                    </h4>
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                      <div className="space-y-1">
                        <span className="text-sm text-gray-600 dark:text-gray-400">
                          الاسم الكامل:
                        </span>
                        <p className="font-medium text-gray-900 dark:text-white">
                          {formData.fullName || "-"}
                        </p>
                      </div>
                      <div className="space-y-1">
                        <span className="text-sm text-gray-600 dark:text-gray-400">
                          رقم الهوية:
                        </span>
                        <p className="font-medium text-gray-900 dark:text-white">
                          {formData.nationalId || "-"}
                        </p>
                      </div>
                      <div className="space-y-1">
                        <span className="text-sm text-gray-600 dark:text-gray-400">
                          الجنسية:
                        </span>
                        <p className="font-medium text-gray-900 dark:text-white">
                          {formData.nationality || "-"}
                        </p>
                      </div>
                      <div className="space-y-1">
                        <span className="text-sm text-gray-600 dark:text-gray-400">
                          البريد الإلكتروني:
                        </span>
                        <p className="font-medium text-gray-900 dark:text-white">
                          {formData.email || "-"}
                        </p>
                      </div>
                      <div className="space-y-1">
                        <span className="text-sm text-gray-600 dark:text-gray-400">
                          رقم الهاتف:
                        </span>
                        <p className="font-medium text-gray-900 dark:text-white">
                          {formData.phone || "-"}
                        </p>
                      </div>
                    </div>
                  </div>

                  {/* بيانات الاستثمار */}
                  <div>
                    <h4 className="font-semibold text-gray-900 dark:text-white mb-3 pb-2 border-b border-gray-200 dark:border-gray-700">
                      بيانات الاستثمار:
                    </h4>
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                      <div className="space-y-1">
                        <span className="text-sm text-gray-600 dark:text-gray-400">
                          المبلغ:
                        </span>
                        <p className="font-medium text-gray-900 dark:text-white">
                          {formData.investmentAmount
                            ? `${formData.investmentAmount} مليون جنيه`
                            : "-"}
                        </p>
                      </div>
                      <div className="space-y-1">
                        <span className="text-sm text-gray-600 dark:text-gray-400">
                          الشركة:
                        </span>
                        <p className="font-medium text-gray-900 dark:text-white">
                          {formData.companyName || "-"}
                        </p>
                      </div>
                      {selectedSector && (
                        <div className="space-y-1">
                          <span className="text-sm text-gray-600 dark:text-gray-400">
                            القطاع:
                          </span>
                          <p className="font-medium text-gray-900 dark:text-white">
                            {selectedSector[`sector_${currentLang}`] ||
                              selectedSector.sector_ar ||
                              "-"}
                          </p>
                        </div>
                      )}
                    </div>
                  </div>

                  {/* المستندات */}
                  <div>
                    <h4 className="font-semibold text-gray-900 dark:text-white mb-3 pb-2 border-b border-gray-200 dark:border-gray-700">
                      المستندات المرفقة:
                    </h4>
                    <div className="space-y-2">
                      {Object.entries(files).map(([key, file]) => (
                        <div
                          key={key}
                          className="flex items-center justify-between p-3 bg-white dark:bg-gray-900 rounded-lg border border-gray-200 dark:border-gray-700"
                        >
                          <div className="flex items-center gap-3">
                            <div
                              className={`p-2 rounded-lg ${
                                file
                                  ? "bg-green-100 dark:bg-green-900/30"
                                  : "bg-red-100 dark:bg-red-900/30"
                              }`}
                            >
                              {file ? (
                                <CheckCircle className="w-5 h-5 text-green-600 dark:text-green-400" />
                              ) : (
                                <AlertCircle className="w-5 h-5 text-red-600 dark:text-red-400" />
                              )}
                            </div>
                            <div>
                              <p className="font-medium text-gray-900 dark:text-white">
                                {translations.ar[key]}
                              </p>
                              <p
                                className={`text-sm ${
                                  file
                                    ? "text-green-600 dark:text-green-400"
                                    : "text-red-600 dark:text-red-400"
                                }`}
                              >
                                {file ? file.name : "غير متوفر"}
                              </p>
                            </div>
                          </div>
                          {file && (
                            <span className="text-xs text-gray-500 dark:text-gray-400">
                              {formatFileSize(file.size)}
                            </span>
                          )}
                        </div>
                      ))}
                    </div>
                  </div>
                </div>
              </div>
            )}

            <div className="flex gap-3 pt-6">
              {currentStep > 1 && (
                <Button
                  type="button"
                  variant="outline"
                  onClick={handlePrevious}
                  className="px-6"
                  disabled={isSubmitting}
                >
                  {tr.previous}
                </Button>
              )}
              <div className="flex-1" />
              {currentStep < 4 ? (
                <Button type="button" onClick={handleNext} className="px-6">
                  {tr.next}
                </Button>
              ) : (
                <Button
                  type="submit"
                  className="flex-1"
                  disabled={isSubmitting}
                >
                  {isSubmitting ? (
                    <>
                      <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white mr-2"></div>
                      {tr.submitting}
                    </>
                  ) : (
                    tr.submit
                  )}
                </Button>
              )}
            </div>
          </form>
        </div>
      </div>
    </div>
  );
};

export default InvestmentModal;
